import { useEffect, useState } from "react";

import { isMobile } from "react-device-detect";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "components/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "components/Dialog";
import Editor from "components/Editor";
import { FormField } from "components/Form";

export default function UploadImageDialog({
  isUploadingScanedDocumentSuccess,
}: {
  isUploadingScanedDocumentSuccess: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const form = useFormContext();

  const { control, setValue, getValues } = form;

  useEffect(() => {
    if (isUploadingScanedDocumentSuccess) {
      setOpen(true);
    }
  }, [isUploadingScanedDocumentSuccess]);

  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="min-w-full md:min-w-[800px]! max-h-[calc(100dvh-1rem)] flex flex-col overflow-hidden p-4"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>{t("session.uploadImageDialog.title")}</DialogHeader>
          <div className="flex-1 min-h-0 mt-4 overflow-hidden [&_.ProseMirror]:max-h-[calc(100dvh-22rem)] [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:overscroll-contain">
            <FormField
              control={control}
              name="uploadedReport"
              render={({ field }) => <Editor content={field.value} onChange={field.onChange} />}
            />
          </div>

          <DialogFooter className="justify-between gap-2">
            <Button
              className="flex-1 md:flex-none md:w-60 min-h-12"
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              {t("button.close")}
            </Button>
            <Button
              className="flex-1 md:flex-none md:w-60 min-h-12"
              type="button"
              variant="primary"
              onClick={() => {
                const reportValue = getValues("report") || "";
                const uploadedReportValue = getValues("uploadedReport") || "";
                const separator = reportValue && uploadedReportValue ? "<p><br></p>" : "";
                setValue("report", `${reportValue}${separator}${uploadedReportValue}`);
                setOpen(false);
              }}
            >
              {t("session.uploadImageDialog.useThisDocument")}{" "}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="min-w-full md:min-w-[800px]! max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>{t("session.uploadImageDialog.title")}</DialogHeader>
        <div className="flex-1 min-h-0 mt-4 overflow-hidden [&_.ProseMirror]:max-h-[calc(100dvh-22rem)] [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:overscroll-contain">
          <FormField
            control={control}
            name="uploadedReport"
            render={({ field }) => <Editor content={field.value} onChange={field.onChange} />}
          />
        </div>

        <DialogFooter className="justify-between gap-2">
          <Button
            className="flex-1 md:flex-none md:w-60"
            type="button"
            variant="secondary"
            onClick={() => setOpen(false)}
          >
            {t("button.close")}
          </Button>
          <Button
            className="flex-1 md:flex-none md:w-60 min-h-12"
            type="button"
            variant="primary"
            onClick={() => {
              const reportValue = getValues("report") || "";
              const uploadedReportValue = getValues("uploadedReport") || "";
              const separator = reportValue && uploadedReportValue ? "<p><br></p>" : "";
              setValue("report", `${reportValue}${separator}${uploadedReportValue}`);
              setOpen(false);
            }}
          >
            {t("session.uploadImageDialog.useThisDocument")}{" "}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
